import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { LEVELS, EVEIL_LEVELS } from "@/data/levels";
import { RELICS } from "@/data/relics";
import { Sparkles, Lock, Check, ChevronLeft, BookOpen, Users, Wrench, Trophy, Zap, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";
import { playClick } from "@/lib/sound";
import { localizeLevel, localizeRelic } from "@/i18n/game";

export default function LevelMap() {
  const { t } = useTranslation();
  const { player, loading, switchMode, familyId } = usePlayer();
  const navigate = useNavigate();
  const [daily, setDaily] = useState(null);
  const [board, setBoard] = useState([]);
  const [soloBest, setSoloBest] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!player) navigate("/profile");
  }, [loading, player, navigate]);

  useEffect(() => {
    api.get("/daily-challenge").then((r) => setDaily(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (familyId) {
      api.get(`/daily-challenge/leaderboard/${familyId}`).then((r) => setBoard(r.data.board || [])).catch(() => {});
    } else if (player?.id) {
      api.get(`/daily-challenge/best/${player.id}`).then((r) => setSoloBest(r.data.seconds)).catch(() => {});
    }
  }, [familyId, daily, player?.id]);

  if (loading || !player) return null;

  const completed = new Set(player.levels_completed || []);
  const unlockedCount = completed.size;

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200 transition-colors" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 sm:gap-4 text-sm">
          <div className="glass rounded-full px-3 py-1 flex items-center gap-2" data-testid="hud-shards">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="font-bold text-amber-200">{player.memory_shards || 0}</span>
            <span className="text-stone-400 text-xs">{t("levels.shards")}</span>
          </div>
          <Link to="/grimoire" className="text-stone-300 hover:text-amber-200 flex items-center gap-1" data-testid="nav-grimoire">
            <BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.grimoire")}</span>
          </Link>
          <Link to="/achievements" className="text-stone-300 hover:text-amber-200 flex items-center gap-1" data-testid="nav-achievements">
            <Award className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.achievements")}</span>
          </Link>
          <Link to="/family" className="text-stone-300 hover:text-amber-200 flex items-center gap-1" data-testid="nav-family">
            <Users className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.family")}</span>
          </Link>
          <Link to="/editor" className="text-stone-300 hover:text-amber-200 flex items-center gap-1" data-testid="nav-editor">
            <Wrench className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.editor")}</span>
          </Link>
          <Link to="/parental" className="text-stone-300 hover:text-amber-200 flex items-center gap-1" data-testid="nav-parental">
            <Users className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.parents")}</span>
          </Link>
          <SoundToggle />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="mb-8 flex items-center gap-5">
          {player.custom_portrait && (
            <img
              src={`data:image/png;base64,${player.custom_portrait}`}
              alt={t("levels.portrait_alt", { name: player.name })}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-amber-400/60 shard-glow object-cover"
              data-testid="header-custom-portrait"
            />
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("levels.kicker")}</div>
            <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
              {t("levels.hello_1")}<span className="italic text-amber-300">{player.name}</span>
            </h2>
            <p className="mt-2 text-stone-300">{t("levels.subtitle")}</p>
          </div>
        </div>

        {daily && player.mode !== "eveil" && (
          <section className="mb-8 relative overflow-hidden rounded-3xl border border-fuchsia-400/25 p-5 sm:p-6" data-testid="daily-challenge">
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/40 via-stone-900/60 to-amber-900/20" aria-hidden />
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-bold">
                  <Trophy className="w-3.5 h-3.5" /> {t("levels.daily_kicker", { date: daily.date })}
                </div>
                <h3 className="mt-1 font-serif text-2xl font-bold text-stone-50">
                  {(() => {
                    const raw = LEVELS.find(l => l.id === daily.level_id);
                    return raw ? localizeLevel(raw, t).title : t("levels.daily_title_fallback");
                  })()}
                </h3>
                <p className="text-sm text-stone-300 mt-1">{t("levels.daily_desc")}</p>
              </div>
              <Button
                onClick={() => navigate(`/play/${daily.level_id}?daily=1`)}
                className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold rounded-full px-6"
                data-testid="btn-play-daily"
              >
                <Trophy className="w-4 h-4 mr-2" /> {t("levels.daily_cta")}
              </Button>
            </div>
            {board.length > 0 && (
              <div className="relative z-10 mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="daily-leaderboard">
                {board.slice(0, 4).map((row, i) => (
                  <div key={row.player_id} className="rounded-xl bg-black/40 border border-white/10 px-3 py-2 flex items-center gap-2">
                    <span className="text-xs font-serif font-black text-fuchsia-300">#{i + 1}</span>
                    <div className="flex-1 truncate">
                      <div className="text-sm text-stone-100 font-bold truncate">{row.name}</div>
                      <div className="text-xs font-mono text-stone-400">{Math.floor(row.seconds / 60)}:{String(row.seconds % 60).padStart(2, "0")}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!familyId && soloBest !== null && (
              <div className="relative z-10 mt-4 rounded-xl bg-black/40 border border-fuchsia-400/30 px-4 py-3 flex items-center gap-3" data-testid="daily-solo-best">
                <Trophy className="w-4 h-4 text-fuchsia-300" />
                <span className="text-sm text-stone-200">{t("levels.daily_solo_best")}</span>
                <span className="font-mono text-lg font-black text-fuchsia-200">
                  {Math.floor(soloBest / 60)}:{String(soloBest % 60).padStart(2, "0")}
                </span>
              </div>
            )}
          </section>
        )}

        <div className="glass rounded-2xl p-2 inline-flex mb-8 flex-wrap" role="tablist" aria-label={t("profile.mode_label")}>
          {[
            {k:"eveil",     l:t("profile.mode_eveil"),     age:t("profile.mode_age_toddler")},
            {k:"discovery", l:t("profile.mode_discovery"), age:t("profile.mode_age_child")},
            {k:"engineer",  l:t("profile.mode_engineer"),  age:t("profile.mode_age_teen")},
          ].map(m => (
            <button
              key={m.k}
              onClick={() => switchMode(m.k)}
              data-testid={`switch-mode-${m.k}`}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex flex-col items-center leading-tight ${
                player.mode === m.k ? "bg-amber-400 text-stone-950" : "text-stone-300 hover:text-stone-100"
              }`}
            >
              <span>{m.l}</span>
              <span className={`text-[10px] font-normal ${player.mode === m.k ? "text-stone-700" : "text-stone-500"}`}>{m.age}</span>
            </button>
          ))}
        </div>

        {player.mode === "eveil" && (
          <section className="mb-8" data-testid="eveil-section">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-bold">{t("levels.eveil_kicker")}</div>
              <p className="mt-1 text-sm text-stone-300">{t("levels.eveil_desc")}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {EVEIL_LEVELS.map((rawLvl, idx) => {
                const lvl = localizeLevel(rawLvl, t);
                const isDone = completed.has(lvl.id);
                return (
                  <motion.div key={lvl.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
                    <button
                      onClick={() => { playClick(); navigate(`/play/${lvl.id}`); }}
                      data-testid={`level-card-${lvl.id}`}
                      className="w-full text-left glass rounded-3xl p-6 relative overflow-hidden group hover:border-fuchsia-400/50 hover:-translate-y-0.5 transition-all border-fuchsia-400/20"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-fuchsia-300/80 font-bold">{lvl.subtitle}</div>
                          <div className="mt-1 font-serif text-3xl font-bold text-stone-50">{lvl.title}</div>
                        </div>
                        {isDone ? (
                          <div className="w-11 h-11 rounded-full bg-emerald-500/25 border border-emerald-400 flex items-center justify-center">
                            <Check className="w-6 h-6 text-emerald-300" />
                          </div>
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-fuchsia-500/25 border border-fuchsia-400 flex items-center justify-center shard-glow">
                            <Sparkles className="w-5 h-5 text-fuchsia-200" />
                          </div>
                        )}
                      </div>
                      <div className="mt-4 text-sm text-stone-400 font-sans">+{lvl.reward.shards} {t("levels.shards")}</div>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {player.mode !== "eveil" && (
        <div className="grid sm:grid-cols-2 gap-5">
          {LEVELS.map((rawLvl, idx) => {
            const lvl = localizeLevel(rawLvl, t);
            const isLocked = idx > 0 && !completed.has(LEVELS[idx - 1].id);
            const isDone = completed.has(lvl.id);
            const relic = RELICS[lvl.reward.relic] ? localizeRelic(lvl.reward.relic, RELICS[lvl.reward.relic], t) : null;
            return (
              <motion.div
                key={lvl.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
              >
                <div
                  role="button"
                  tabIndex={isLocked ? -1 : 0}
                  aria-disabled={isLocked}
                  onClick={() => !isLocked && (playClick(), navigate(`/play/${lvl.id}`))}
                  onKeyDown={(e) => { if (!isLocked && (e.key === "Enter" || e.key === " ")) { playClick(); navigate(`/play/${lvl.id}`); } }}
                  data-testid={`level-card-${lvl.id}`}
                  className={`w-full text-left glass rounded-3xl p-6 relative overflow-hidden group ${
                    isLocked ? "opacity-40 cursor-not-allowed" : "hover:border-amber-400/50 hover:-translate-y-0.5 transition-all cursor-pointer"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-amber-300/80 font-bold">{lvl.subtitle}</div>
                      <div className="mt-1 font-serif text-2xl font-bold text-stone-50">{lvl.title}</div>
                    </div>
                    {isDone ? (
                      <div className="w-9 h-9 rounded-full bg-emerald-500/25 border border-emerald-400 flex items-center justify-center">
                        <Check className="w-5 h-5 text-emerald-300" />
                      </div>
                    ) : isLocked ? (
                      <Lock className="w-5 h-5 text-stone-400" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-amber-500/25 border border-amber-400 flex items-center justify-center shard-glow">
                        <Sparkles className="w-4 h-4 text-amber-200" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3 text-xs text-stone-400 font-sans">
                    <div>+{lvl.reward.shards} {t("levels.shards")} · {relic?.name}</div>
                    {isDone && player.mode !== "eveil" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/play/${lvl.id}?flash=1`); }}
                        className="rounded-full px-3 py-1 bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-200 hover:bg-fuchsia-500/30 flex items-center gap-1 text-xs font-bold"
                        data-testid={`btn-flash-${lvl.id}`}
                      >
                        <Zap className="w-3 h-3" /> {t("levels.flash_challenge")}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        )}

        <section className="mt-14">
          <h3 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("levels.relics_unlocked")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.values(RELICS).map((rawRelic) => {
              const r = localizeRelic(rawRelic.id, rawRelic, t);
              const owned = (player.relics_unlocked || []).includes(r.id);
              return (
                <div
                  key={r.id}
                  className={`glass rounded-2xl p-4 ${owned ? "border-amber-400/40" : "opacity-40"}`}
                  data-testid={`relic-${r.id}`}
                >
                  <div className="text-3xl">{r.icon}</div>
                  <div className="mt-2 font-serif font-bold text-stone-50">{r.name}</div>
                  <p className="text-xs text-stone-400 mt-1 leading-relaxed">{r.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <div className="mt-10 text-xs text-stone-500 font-sans">
          {t("levels.progress", {
            done: unlockedCount,
            total: player.mode === "eveil" ? EVEIL_LEVELS.length : LEVELS.length,
            mode: player.mode === "engineer" ? t("levels.mode_mapping_engineer")
                : player.mode === "eveil" ? t("levels.mode_mapping_eveil")
                : t("levels.mode_mapping_discovery"),
          })}
        </div>
      </main>
    </div>
  );
}
